import type { ComuneFull } from '@/data';
import type { ChoroplethLayerDef } from './types';
import { qualitativeLabel } from '@/lib/qualitative';

// Кол-во знаков после запятой по типу единицы измерения
function decimalsFor(unit: string): number {
  if (unit === '°C') return 1;
  if (unit === '%') return 2;
  if (unit === 'Мбит/с') return 0;
  if (unit === 'м' || unit === 'мм' || unit.includes('€') || unit.includes('чел'))
    return 0;
  if (unit === 'км') return 1;
  if (unit.startsWith('дн')) return 0;
  return 1;
}

// Единицы записаны в layers-config по-русски — это конфиг слоёв, а не текст
// интерфейса, и переводить его целиком ради двух символов не стоит. Здесь
// переводится только то, что уходит на экран: без этого англоязычный читатель
// видел «606 м» рядом с английской меткой слоя.
const UNIT_EN: Record<string, string> = {
  'м': 'm',
  'мм': 'mm',
  'км': 'km',
  'дн/год': 'd/yr',
  'чел/км²': 'ppl/km²',
  'Мбит/с': 'Mbps',
  '€/год': '€/yr',
  '€/м²': '€/m²',
};

export function fmtVal(v: number | null | undefined, unitRaw: string, lang: 'en' | 'ru' = 'ru'): string {
  if (v == null || !isFinite(v)) return '—';
  const unit = lang === 'en' ? UNIT_EN[unitRaw] ?? unitRaw : unitRaw;
  const d = decimalsFor(unitRaw);
  // Больш ие числа форматируем с локалью пользователя (12 345 / 12,345)
  if (Math.abs(v) >= 1000 && d === 0) {
    return `${Math.round(v).toLocaleString(lang === 'ru' ? 'ru' : 'en')} ${unit}`;
  }
  return `${v.toFixed(d)} ${unit}`;
}

// Short i18n labels used inside tooltip HTML. Kept inline (not via react-i18next)
// because tooltips render as raw HTML strings into Leaflet outside React's tree.
const TOOLTIP_STRINGS = {
  ru: { noData: 'нет данных', avg: 'ср' },
  en: { noData: 'no data', avg: 'avg' },
} as const;

// Агрегат по списку comuni в провинции/регионе
export interface Aggregate {
  min: number;
  max: number;
  avg: number;
  count: number;
}

export function aggregateForLayer(
  comuni: ComuneFull[],
  layerDef: ChoroplethLayerDef,
): Aggregate | null {
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let count = 0;
  for (const c of comuni) {
    const v = layerDef.getValue(c);
    if (v === null || !isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
    count++;
  }
  if (count === 0) return null;
  return { min, max, avg: sum / count, count };
}

// HTML контент tooltip для одной comune (high zoom / heatmap)
export function comuneTooltipHtml(
  comune: ComuneFull,
  layerDef: ChoroplethLayerDef,
  lang: 'en' | 'ru' = 'ru',
  // Локализованное имя слоя. Без него тултип показывал русскую константу из
  // layers-config даже англоязычному читателю: переключатель слоёв давно
  // переведён через layer.label.*, а тултипы рендерятся сырым HTML мимо
  // react-i18next и перевод до них не доходил.
  label?: string,
): string {
  const v = layerDef.getValue(comune);
  const valStr = fmtVal(v, layerDef.unit, lang);
  const qual = qualitativeLabel(layerDef.id, v, lang);
  const name = label ?? layerDef.label;
  return (
    `<div class="font-semibold text-slate-900">${escapeHtml(comune.name)}</div>` +
    `<div class="text-slate-600">${escapeHtml(name)}: <span class="font-semibold text-slate-900">${valStr}</span></div>` +
    (qual ? `<div class="text-[11px] text-slate-500 italic mt-0.5">${escapeHtml(qual)}</div>` : '')
  );
}

// HTML контент tooltip для агрегата по провинции (low zoom)
export function provinceTooltipHtml(
  provAcr: string,
  comuni: ComuneFull[],
  layerDef: ChoroplethLayerDef,
  lang: 'en' | 'ru' = 'ru',
  // Термин единицы приходит из meta.admin_terms активной страны. Он вынесен
  // отдельной строкой «термин: N», а не поставлен после числа, потому что в
  // русской локали «141 муниципалитеты» требует склонения, которого в meta нет.
  unitPlural = 'comuni',
  label?: string,
): string {
  const s = TOOLTIP_STRINGS[lang];
  const layerName = label ?? layerDef.label;
  const agg = aggregateForLayer(comuni, layerDef);
  const head = `<div class="font-semibold text-slate-900">${escapeHtml(provAcr)}</div>` +
    `<div class="text-[10px] text-slate-500">${escapeHtml(unitPlural)}: ${comuni.length}</div>`;
  if (!agg) {
    return head + `<div class="text-slate-500">${escapeHtml(layerName)}: ${s.noData}</div>`;
  }
  const rangeStr =
    agg.min === agg.max
      ? fmtVal(agg.avg, layerDef.unit, lang)
      : `${fmtVal(agg.min, layerDef.unit, lang)} — ${fmtVal(agg.max, layerDef.unit, lang)}`;
  const qual = qualitativeLabel(layerDef.id, agg.avg, lang);
  return (
    head +
    `<div class="text-slate-600">${escapeHtml(layerName)}: <span class="font-semibold text-slate-900">${rangeStr}</span></div>` +
    `<div class="text-[10px] text-slate-500">${s.avg} ${fmtVal(agg.avg, layerDef.unit, lang)}</div>` +
    (qual ? `<div class="text-[11px] text-slate-500 italic mt-0.5">${escapeHtml(qual)}</div>` : '')
  );
}

// HTML контент tooltip для агрегата по району (SLL / comunità comprensoriale).
export function districtTooltipHtml(
  distName: string,
  comuni: ComuneFull[],
  layerDef: ChoroplethLayerDef,
  lang: 'en' | 'ru' = 'ru',
  unitPlural = 'comuni',
  label?: string,
): string {
  const s = TOOLTIP_STRINGS[lang];
  const layerName = label ?? layerDef.label;
  const agg = aggregateForLayer(comuni, layerDef);
  const head = `<div class="font-semibold text-slate-900">${escapeHtml(distName)}</div>` +
    `<div class="text-[10px] text-slate-500">${escapeHtml(unitPlural)}: ${comuni.length}</div>`;
  if (!agg) {
    return head + `<div class="text-slate-500">${escapeHtml(layerName)}: ${s.noData}</div>`;
  }
  const rangeStr =
    agg.min === agg.max
      ? fmtVal(agg.avg, layerDef.unit, lang)
      : `${fmtVal(agg.min, layerDef.unit, lang)} — ${fmtVal(agg.max, layerDef.unit, lang)}`;
  const qual = qualitativeLabel(layerDef.id, agg.avg, lang);
  return (
    head +
    `<div class="text-slate-600">${escapeHtml(layerName)}: <span class="font-semibold text-slate-900">${rangeStr}</span></div>` +
    `<div class="text-[10px] text-slate-500">${s.avg} ${fmtVal(agg.avg, layerDef.unit, lang)}</div>` +
    (qual ? `<div class="text-[11px] text-slate-500 italic mt-0.5">${escapeHtml(qual)}</div>` : '')
  );
}

// HTML контент tooltip для агрегата по региону NUTS-1 (zoom 6–7).
export function regionTooltipHtml(
  regName: string,
  comuni: ComuneFull[],
  layerDef: ChoroplethLayerDef,
  lang: 'en' | 'ru' = 'ru',
  unitPlural = 'comuni',
  label?: string,
): string {
  const s = TOOLTIP_STRINGS[lang];
  const layerName = label ?? layerDef.label;
  const agg = aggregateForLayer(comuni, layerDef);
  const head = `<div class="font-semibold text-slate-900">${escapeHtml(regName)}</div>` +
    `<div class="text-[10px] text-slate-500">${escapeHtml(unitPlural)}: ${comuni.length}</div>`;
  if (!agg) {
    return head + `<div class="text-slate-500">${escapeHtml(layerName)}: ${s.noData}</div>`;
  }
  const rangeStr =
    agg.min === agg.max
      ? fmtVal(agg.avg, layerDef.unit, lang)
      : `${fmtVal(agg.min, layerDef.unit, lang)} — ${fmtVal(agg.max, layerDef.unit, lang)}`;
  const qual = qualitativeLabel(layerDef.id, agg.avg, lang);
  return (
    head +
    `<div class="text-slate-600">${escapeHtml(layerName)}: <span class="font-semibold text-slate-900">${rangeStr}</span></div>` +
    `<div class="text-[10px] text-slate-500">${s.avg} ${fmtVal(agg.avg, layerDef.unit, lang)}</div>` +
    (qual ? `<div class="text-[11px] text-slate-500 italic mt-0.5">${escapeHtml(qual)}</div>` : '')
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return ch;
    }
  });
}
